import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API_URL } from "./utils/utils";
import Header from "../components/header";
import Footer from "../components/footer";
import SeoLinksSection from "../components/SeoLinksSection";

export default function SeoPage() {
  const { slug } = useParams();
  const [seoPage, setSeoPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchSeoPage();
    } else {
      setLoading(false);
      setError("Invalid page slug");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Update meta description and title directly in the DOM to ensure they override defaults
  useEffect(() => {
    if (seoPage) {
      // Update meta description
      if (seoPage.meta_description) {
        let existingMeta = document.querySelector('meta[name="description"]');
        if (existingMeta) {
          existingMeta.setAttribute('content', seoPage.meta_description);
        } else {
          // Create new meta tag if it doesn't exist
          const meta = document.createElement('meta');
          meta.name = 'description';
          meta.content = seoPage.meta_description;
          document.head.appendChild(meta);
        }
      }

      // Update title
      const title = seoPage.meta_title || seoPage.seo_page_title || "SEO Page";
      document.title = title;
    }
  }, [seoPage]);

  const fetchSeoPage = async () => {
    if (!slug) {
      setError("Invalid page slug");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_URL}seo/index_seo_page/${encodeURIComponent(slug)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const contentType = response.headers.get("content-type");
      const isJson = contentType && contentType.includes("application/json");

      if (response.ok && isJson) {
        const data = await response.json();

        if ((data.status === true || data.status === "success") && data.data) {
          // Handle both array and single object responses
          if (Array.isArray(data.data)) {
            // If data is an array, find the page with matching slug or use first item
            const page = data.data.find((p) => p.slug === slug) || data.data[0];
            if (page) {
              setSeoPage(page);
            } else {
              setError("Page not found in response");
              setSeoPage(null);
            }
          } else {
            // If data is a single object
            setSeoPage(data.data);
          }
        } else {
          setError("Invalid response from server");
          setSeoPage(null);
        }
      } else {
        setError(`Failed to load page (Status: ${response.status})`);
        setSeoPage(null);
      }
    } catch (error) {
      console.error("Error fetching SEO page:", error);
      setError("An error occurred while loading the page");
      setSeoPage(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="container py-5">
          <div className="text-center">
            <p>Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !seoPage) {
    return (
      <>
        <Header />
        <div className="container py-5">
          <div className="text-center">
            <h1>Page Not Found</h1>
            <p>{error || "The requested page could not be found."}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoPage.meta_title || seoPage.seo_page_title || "SEO Page"}</title>
        <meta
          name="description"
          content={seoPage.meta_description || ""}
        />
      </Helmet>
      <Header />
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-12">
            <h1 className="mb-4">{seoPage.seo_page_title || "SEO Page"}</h1>
            {seoPage.seo_page_body && (
              <div
                className="seo-page-content"
                dangerouslySetInnerHTML={{ __html: seoPage.seo_page_body }}
              />
            )}
          </div>
        </div>
      </div>
      <SeoLinksSection />
      <Footer />
    </>
  );
}
